// Purer Kern des Tourenoptimierungs-Algorithmus — 1:1 aus technician-date.ts extrahiert.
// Arbeitet ausschließlich auf TourOptimizer*-Plain-Data (kein Singleton-Zugriff, kein Date.now()),
// damit die komplette Zeitrechnung deterministisch unit-testbar ist. Verhaltensänderungen an der
// Optimierung gehören hierher, nicht in den Adapter (technician-date.ts).
import type {
  TourOptimizerContext,
  TourOptimizerInsertion,
  TourOptimizerOperation,
  TourOptimizerResult,
  TourOptimizerScoredRoute,
} from "./tour-optimizer.types";

/**
 * Gesetzliche Pausenregel nach Tour-Gesamtdauer. Lebt im Kern, damit Adapter, Wiedergabe und Tests
 * dieselbe Quelle benutzen — PATour.getLunchBreakTimeConfig delegiert hierher.
 */
export function defaultLunchBreakConfig(tour_minutes: number): { minutes: number; after_tour_minute: number } | undefined {
  if (tour_minutes <= 6 * 60) return undefined;
  return tour_minutes > 9 * 60 ? { minutes: 45, after_tour_minute: 270 } : { minutes: 30, after_tour_minute: 180 };
}

/** Fahrminuten mit Techniker-Faktor in Millisekunden umrechnen (Rundung wie im Original) */
function factoredDrivingMs(driving_minutes: number, ctx: TourOptimizerContext): number {
  return Math.round(driving_minutes * ctx.driving_time_factor) * 60 * 1000;
}

/**
 * Letzter fester Orts-Anker einer (Teil-)Route. Rückwärts iterieren und beim ersten gültigen
 * Treffer zurückgeben — Aufträge ohne Anker-Eigenschaft (NO_LOCATION_CHANGE-Ausnahmen) zählen nicht.
 */
export function getLastAnchorOperation(route: TourOptimizerOperation[]): TourOptimizerOperation | undefined {
  for (let i = route.length - 1; i >= 0; i--) {
    if (route[i].is_location_anchor) {
      return route[i];
    }
  }
  return undefined;
}

export function recursiveInsertionShift(insertions: TourOptimizerInsertion[], shift_by_ms: number): TourOptimizerInsertion[] {
  const flexible_insertions = insertions.filter((ins) => !ins.operation.date_fixed);
  if (!flexible_insertions.length) return [...insertions];

  // Barriere ist nur ein Fixtermin, der zeitlich NACH den zu verschiebenden Aufträgen beginnt.
  // Bereits verstrichene bzw. früher liegende Fixtermine bleiben fest an ihrer Zeit und dürfen
  // nichts umsortieren — sonst zieht die „hinter den Termin schieben"-Rechnung Aufträge über
  // eine negative Lücke in die Vergangenheit zurück
  const first_flexible_start_ms = Math.min(...flexible_insertions.map((ins) => ins.start_time_ms));
  const next_appointment_insertion = insertions.find(
    (ins) => ins.operation.date_fixed && ins.start_time_ms >= first_flexible_start_ms,
  );

  if (!next_appointment_insertion) {
    // Case: No more Appointments -> normal shift till the end (Fixtermine bleiben unverschoben)
    flexible_insertions.map((insertion) => {
      insertion.start_time_ms += shift_by_ms;
      insertion.end_time_ms += shift_by_ms;
    });
    return [...insertions];
  } else {
    // Case more Appointments
    const current_insertion_index = insertions.indexOf(next_appointment_insertion);
    const insertions_before_appointment = insertions.filter((ins) => insertions.indexOf(ins) < current_insertion_index);
    const insertions_after_appointment = insertions.filter((ins) => insertions.indexOf(ins) > current_insertion_index);

    const fitting_insertions: TourOptimizerInsertion[] = [];
    const unfitting_insertions: TourOptimizerInsertion[] = [];
    let new_shift_ms = 0;
    for (const insertion of insertions_before_appointment) {
      // Fixtermine vor der Barriere (bereits verstrichene Termine) bleiben unverändert fest
      if (insertion.operation.date_fixed) {
        fitting_insertions.push(insertion);
        continue;
      }
      const shifted_end_time = insertion.end_time_ms + shift_by_ms;
      if (shifted_end_time <= next_appointment_insertion.start_time_ms) {
        fitting_insertions.push(insertion);
        insertion.start_time_ms += shift_by_ms;
        insertion.end_time_ms += shift_by_ms;
      } else {
        unfitting_insertions.push(insertion);
        new_shift_ms += insertion.end_time_ms - insertion.start_time_ms;
      }
    }

    if (unfitting_insertions.length) {
      const ms_between_last_unfitting_insertion_an_appointment_insertion =
        next_appointment_insertion.start_time_ms - unfitting_insertions[unfitting_insertions.length - 1].end_time_ms;
      const appointment_insertion_duration =
        next_appointment_insertion.end_time_ms - next_appointment_insertion.start_time_ms;
      for (const insertion of unfitting_insertions) {
        const pre_shift_ms =
          appointment_insertion_duration + ms_between_last_unfitting_insertion_an_appointment_insertion + shift_by_ms;
        insertion.start_time_ms += pre_shift_ms;
        insertion.end_time_ms += pre_shift_ms;
      }
    }

    const new_shift_insertions = unfitting_insertions.concat(insertions_after_appointment);
    return fitting_insertions.concat([next_appointment_insertion], recursiveInsertionShift(new_shift_insertions, new_shift_ms));
  }
}

export function shiftInsertionsByMinutes(
  insertions: TourOptimizerInsertion[],
  shift_start_idx: number,
  shift_by_minutes: number,
): TourOptimizerInsertion[] {
  const shift_by_ms = shift_by_minutes * 60000;
  const not_shifted_insertions = insertions.filter((ins) => insertions.indexOf(ins) < shift_start_idx);
  const shifted_insertions = insertions.filter((ins) => insertions.indexOf(ins) >= shift_start_idx);
  return not_shifted_insertions.concat(recursiveInsertionShift(shifted_insertions, shift_by_ms));
}

/**
 * Simuliert den zeitlichen Ablauf einer Route.
 * Prüft ob der Techniker rechtzeitig zu allen Fixterminen kommt.
 *
 * ABLAUF:
 * - Startet zur konfigurierten Arbeitszeit; bei bereits gestarteten Aufträgen wird stattdessen
 *   am (realen bzw. geschätzten) Ende des letzten gestarteten Auftrags geankert
 * - Heutige Touren: nicht gestartete Aufträge beginnen frühestens ab „jetzt"
 * - Iteriert durch die nicht gestarteten Aufträge in Array-Reihenfolge
 * - Bei Fixterminen: verspätete Ankunft macht die Route nicht ungültig, sondern wird als
 *   fixed_lateness_ms aufsummiert — die Routenwahl minimiert Verspätung lexikografisch
 *   (nach removed_count, vor Fahrzeit), sodass pünktliche Permutationen immer gewinnen
 * - Bei normalen Ops: Addiert Fahrzeit + Arbeitszeit
 * - Fixtermine ohne Ortswechsel werden zeitlich eingeschoben
 */
export async function simulateRouteTime(
  route: TourOptimizerOperation[],
  fixed_date_ops_no_location: TourOptimizerOperation[],
  start_location_id: string,
  end_location_id: string | null,
  ctx: TourOptimizerContext,
): Promise<{ total_time_ms: number; fixed_lateness_ms: number }> {
  // Bereits gestartete Aufträge werden NICHT ab Arbeitsbeginn neu simuliert, sondern über ihre
  // echten Zeitstempel geankert (wie in computeOptimizedRoute) — sonst schiebt die Schätzsummen-
  // Simulation die Ankunft hinter erreichbare Fixtermine und verwirft jede Permutation
  const already_started_operations = route.filter((operation) => operation.has_started);
  const not_started_route = route.filter((operation) => !operation.has_started);

  let current_time_ms: number;
  let current_location_id: string;
  let total_work_and_drive_time_ms = 0;

  if (already_started_operations.length) {
    const last_started_operation = already_started_operations[already_started_operations.length - 1];
    current_time_ms = last_started_operation.started_end_time_ms;
    current_location_id = getLastAnchorOperation(already_started_operations)?.location_id || start_location_id;

    // Fahr- und Arbeitszeiten der gestarteten Aufträge fließen weiterhin ins Zeitbudget ein
    for (const started_operation of already_started_operations) {
      total_work_and_drive_time_ms += started_operation.started_drive_and_work_ms;
    }
  } else {
    // Bei Ankunftszeit: Losfahrzeit = konfigurierte Zeit - Fahrzeit zum ersten Auftrag
    let day_start_ms = ctx.configured_start_ms;
    const first_op = not_started_route[0];
    if (ctx.is_arrival_time && first_op?.triggers_location_change) {
      const driving_minutes = (await ctx.getDrivingData(start_location_id, first_op.location_id, true)).duration;
      const driving_time_ms = factoredDrivingMs(driving_minutes, ctx);
      day_start_ms = ctx.configured_start_ms - driving_time_ms;
    }
    if (first_op?.date_fixed) {
      // Fixtermin als erster Auftrag: Abfahrt so wählen, dass die Ankunft pünktlich ist —
      // auch vor dem regulären Arbeitsbeginn (gleiche Logik wie in computeOptimizedRoute)
      const fixed_time_ms = first_op.operation_day_ms;
      const fixed_driving_minutes = first_op.triggers_location_change
        ? (await ctx.getDrivingData(start_location_id, first_op.location_id, true)).duration
        : 0;
      const fixed_driving_time_ms = factoredDrivingMs(fixed_driving_minutes, ctx);
      day_start_ms = Math.min(day_start_ms, fixed_time_ms - fixed_driving_time_ms);
    }
    current_time_ms = day_start_ms;
    current_location_id = start_location_id;
  }

  // Heutige Touren: Nicht gestartete Aufträge können frühestens ab „jetzt" beginnen
  const now_day_ms = ctx.now_day_ms;
  if (now_day_ms != null && now_day_ms > current_time_ms) {
    current_time_ms = now_day_ms;
  }

  // Kopie der Fixtermine ohne Ortswechsel (werden während Simulation "verbraucht")
  let remaining_fixed_no_location = [...fixed_date_ops_no_location];

  // Summierte Verspätung an (noch nicht verstrichenen) Fixterminen dieser Permutation
  let fixed_lateness_ms = 0;

  for (const operation of not_started_route) {
    // Fahrzeit zur nächsten Operation berechnen (falls Ortswechsel)
    let driving_time_ms = 0;
    if (operation.triggers_location_change) {
      const driving_minutes = (await ctx.getDrivingData(current_location_id, operation.location_id, true)).duration;
      driving_time_ms = factoredDrivingMs(driving_minutes, ctx);
    }

    // Vor dieser Operation: Fixtermine ohne Ortswechsel einfügen die zeitlich dazwischen liegen
    const arrival_time_ms = current_time_ms + driving_time_ms;
    for (const fixed_op of [...remaining_fixed_no_location]) {
      const fixed_time_ms = fixed_op.operation_day_ms;
      const op_work_time_ms = operation.work_time_ms || 0;

      // Wenn der Fixtermin VOR dem Ende der aktuellen Operation liegt
      if (fixed_time_ms < arrival_time_ms + op_work_time_ms) {
        // Fixtermin einschieben — Wartezeit bis Fixtermin (falls früher angekommen)
        if (current_time_ms < fixed_time_ms) {
          current_time_ms = fixed_time_ms;
        }
        // Arbeitszeit des Fixtermins addieren
        const fixed_work_time_ms = fixed_op.work_time_ms || 0;
        current_time_ms += fixed_work_time_ms;
        total_work_and_drive_time_ms += fixed_work_time_ms;

        remaining_fixed_no_location = remaining_fixed_no_location.filter((op) => op !== fixed_op);
      }
    }

    // Jetzt die eigentliche Operation verarbeiten
    if (operation.date_fixed) {
      // FIXTERMIN MIT ORTSWECHSEL
      const fixed_time_ms = operation.operation_day_ms;
      const arrival_time = current_time_ms + driving_time_ms;

      // Fixtermine, deren Uhrzeit heute bereits verstrichen ist, können nicht mehr „verpasst"
      // werden — sie zählen nicht in die Verspätung, sonst würde Vergangenheit, an der niemand
      // mehr etwas ändern kann, die Routenwahl verzerren
      const fixed_time_already_passed = now_day_ms != null && now_day_ms > fixed_time_ms;

      if (arrival_time > fixed_time_ms && !fixed_time_already_passed) {
        // Techniker kommt zu spät zum Fixtermin → Verspätung aufsummieren statt die Route
        // hart zu verwerfen. Damit blockiert ein unvermeidbar verspäteter Fixtermin (z. B.
        // „totes Fenster" kurz vor dem Termin an heutigen Tagen) nicht mehr die gesamte
        // Tagesplanung — es gewinnt die Permutation mit der geringsten Verspätung
        fixed_lateness_ms += arrival_time - fixed_time_ms;
      }

      // Wartezeit bis Fixtermin — bei Verspätung bzw. verstrichenem Fixtermin ab Ankunftszeit weiterrechnen
      current_time_ms = Math.max(fixed_time_ms, arrival_time);
      total_work_and_drive_time_ms += driving_time_ms;
    } else {
      // NORMALE OPERATION
      current_time_ms += driving_time_ms;
      total_work_and_drive_time_ms += driving_time_ms;
    }

    // Arbeitszeit addieren
    const work_time_ms = operation.work_time_ms || 0;
    current_time_ms += work_time_ms;
    total_work_and_drive_time_ms += work_time_ms;

    // Location aktualisieren
    if (operation.triggers_location_change) {
      current_location_id = operation.location_id;
    }
  }

  // Restliche Fixtermine ohne Ortswechsel am Ende
  for (const fixed_op of remaining_fixed_no_location) {
    const fixed_time_ms = fixed_op.operation_day_ms;
    if (current_time_ms < fixed_time_ms) {
      current_time_ms = fixed_time_ms;
    }
    const work_time_ms = fixed_op.work_time_ms || 0;
    current_time_ms += work_time_ms;
    total_work_and_drive_time_ms += work_time_ms;
  }

  // Heimfahrt
  if (end_location_id) {
    const home_driving_minutes = (await ctx.getDrivingData(current_location_id, end_location_id, true)).duration;
    total_work_and_drive_time_ms += factoredDrivingMs(home_driving_minutes, ctx);
  }

  return { total_time_ms: total_work_and_drive_time_ms, fixed_lateness_ms: fixed_lateness_ms };
}

/**
 * Baut die Route mit den Extra-Aufträgen (ohne Ortswechsel) zusammen — Aufträge mit gepinntem
 * Tour-Index ziehen so viele Extra-Aufträge vor sich, bis ihr Index erreicht ist.
 * Auch der Adapter nutzt diese Reihenfolge (Prioritäts-Zähler sind reihenfolge-sensitiv).
 */
export function buildRouteWithExtraOperations(
  route: TourOptimizerOperation[],
  extra_operations_without_location_change: TourOptimizerOperation[],
): TourOptimizerOperation[] {
  const extra_ops_no_location = [...extra_operations_without_location_change];
  let route_with_extra_operations: TourOptimizerOperation[] = [];
  for (const operation of route) {
    const op_tour_idx = operation.tour_index;
    if (op_tour_idx != -1) {
      let diff = op_tour_idx - route.indexOf(operation);
      while (diff > 0 && extra_ops_no_location.length) {
        route_with_extra_operations.push(extra_ops_no_location.splice(0, 1)[0]);
        diff -= 1;
      }
    }
    route_with_extra_operations.push(operation);
  }
  route_with_extra_operations = route_with_extra_operations.concat(extra_ops_no_location);
  return route_with_extra_operations;
}

/**
 * Prüft ob eine Route ins Zeitbudget passt und entfernt ggf. Aufträge.
 * Erwartet die bereits mit Extra-Aufträgen zusammengebaute Route (buildRouteWithExtraOperations)
 * sowie die nach Uhrzeit sortierten Fixtermine ohne Ortswechsel.
 */
export async function findTimeConstrainedPartialRoute(
  route_with_extra_operations: TourOptimizerOperation[],
  fixed_date_ops_no_location: TourOptimizerOperation[],
  start_location_id: string,
  end_location_id: string | null,
  max_tour_time_ms: number,
  highest_remove_count: number,
  ctx: TourOptimizerContext,
): Promise<{
  partial_route: TourOptimizerOperation[];
  removed_operations: TourOptimizerOperation[];
  fixed_date_operations_without_location_change: TourOptimizerOperation[];
  fixed_lateness_ms: number;
} | null> {
  // Zeitliche Simulation der Route — bei Budgetüberschreitung schrittweise entfernbare
  // Aufträge herausnehmen
  const removed_operations: TourOptimizerOperation[] = [];
  let current_partial_route = route_with_extra_operations;
  let fixed_lateness_ms = 0;

  while (
    (current_partial_route.length || fixed_date_ops_no_location.length) &&
    (highest_remove_count == -1 || removed_operations.length <= highest_remove_count)
  ) {
    const simulation_result = await simulateRouteTime(
      current_partial_route,
      fixed_date_ops_no_location,
      start_location_id,
      end_location_id,
      ctx,
    );

    // Fixtermin-Verspätungen verwerfen die Permutation nicht mehr — sie fließen als
    // Sortierschlüssel in die Routenwahl ein (siehe sortScoredRoutes)
    fixed_lateness_ms = simulation_result.fixed_lateness_ms;

    if (simulation_result.total_time_ms < max_tour_time_ms) {
      // Route passt ins Budget
      return {
        partial_route: current_partial_route,
        removed_operations,
        fixed_date_operations_without_location_change: fixed_date_ops_no_location,
        fixed_lateness_ms,
      };
    }

    // Route zu lang → letzte entfernbare Operation finden und entfernen
    // (Fixtermine sind NICHT entfernbar — sie müssen an diesem Tag erledigt werden)
    let last_removable_operation: TourOptimizerOperation = null;
    for (const operation of current_partial_route) {
      if (!operation.date_fixed && operation.removable) {
        last_removable_operation = operation;
      }
    }

    if (!last_removable_operation) {
      // Keine entfernbare Operation mehr → Route so zurückgeben wie sie ist
      return {
        partial_route: current_partial_route,
        removed_operations,
        fixed_date_operations_without_location_change: fixed_date_ops_no_location,
        fixed_lateness_ms,
      };
    }

    // Operation entfernen
    const last_removed_idx = current_partial_route.indexOf(last_removable_operation);
    current_partial_route = current_partial_route.filter((op) => op !== last_removable_operation);

    // Nach Entfernung: Aufträge mit festem tour_index ggf. verschieben
    if (current_partial_route.length !== last_removed_idx) {
      const all_spliced_ops: { op: TourOptimizerOperation; idx: number }[] = [];
      for (let idx = last_removed_idx; idx <= current_partial_route.length - 1; idx++) {
        const op = current_partial_route[idx - all_spliced_ops.length];
        if (op && op.tour_index !== -1) {
          all_spliced_ops.push({ op: current_partial_route.splice(idx - all_spliced_ops.length, 1)[0], idx: idx });
        }
      }
      for (const spliced_op of all_spliced_ops) {
        current_partial_route.splice(spliced_op.idx + 1, 0, spliced_op.op);
      }
    }

    removed_operations.push(last_removable_operation);
  }

  // Abbruch wenn zu viele Aufträge entfernt werden müssten
  if (highest_remove_count !== -1 && removed_operations.length > highest_remove_count) {
    return null;
  }

  return {
    partial_route: current_partial_route,
    removed_operations,
    fixed_date_operations_without_location_change: fixed_date_ops_no_location,
    fixed_lateness_ms,
  };
}

/** Score einer Route = Gesamtdistanz und -dauer über alle Ortswechsel inkl. Heimfahrt */
export async function routeScore(
  route: Pick<TourOptimizerOperation, 'triggers_location_change' | 'location_id'>[],
  start_location_id: string,
  end_location_id: string | null,
  ctx: TourOptimizerContext,
): Promise<{ distance: number; duration: number }> {
  const score = { distance: 0, duration: 0 };
  let current_location_id = start_location_id;
  for (const operation of route) {
    if (operation.triggers_location_change) {
      const distance = await ctx.getDrivingData(current_location_id, operation.location_id, false);
      score.distance += distance.distance;
      score.duration += distance.duration;
      current_location_id = operation.location_id;
    }
  }
  if (end_location_id) {
    const home_distance = await ctx.getDrivingData(current_location_id, end_location_id, false);
    score.distance += home_distance.distance;
    score.duration += home_distance.duration;
  }
  return score;
}

export function sortScoredRoutes(scored_routes: TourOptimizerScoredRoute[]): TourOptimizerScoredRoute[] {
  const sortScoredRoutesByTime = (scored_permutation_a: TourOptimizerScoredRoute, scored_permutation_b: TourOptimizerScoredRoute) => {
    if (scored_permutation_a.removed_count < scored_permutation_b.removed_count) return -1;
    if (scored_permutation_a.removed_count > scored_permutation_b.removed_count) return 1;

    // Fixtermin-Treue schlägt Fahrzeitersparnis: Erst wenn zwei Permutationen gleich viele
    // Aufträge entfernen UND gleich (un)pünktlich sind, entscheidet die Fahrzeit
    const lateness_a = scored_permutation_a.fixed_lateness_ms || 0;
    const lateness_b = scored_permutation_b.fixed_lateness_ms || 0;
    if (lateness_a < lateness_b) return -1;
    if (lateness_a > lateness_b) return 1;

    const primary_score_a = scored_permutation_a.score.duration;
    const primary_score_b = scored_permutation_b.score.duration;
    const secondary_score_a = scored_permutation_a.score.distance;
    const secondary_score_b = scored_permutation_b.score.distance;

    if (primary_score_a < primary_score_b) return -1;
    if (primary_score_a > primary_score_b) return 1;
    if (primary_score_a == primary_score_b) {
      if (secondary_score_a < secondary_score_b) return -1;
      if (secondary_score_a >= secondary_score_b) return 1;
    }
    return 0;
  };

  return scored_routes.sort(sortScoredRoutesByTime);
}

function insertionMatchesSLAConstraint(insertion: TourOptimizerInsertion): boolean {
  if (insertion.operation.sla_end_limit_ms == null) return true;
  return insertion.end_time_ms <= insertion.operation.sla_end_limit_ms;
}

// "No Tour should go from City A to City B and then back to A"
function insertionsMatchesSameCityConstraint(
  insertions: TourOptimizerInsertion[],
  last_visited_city_before_insertions?: string,
): boolean {
  const location_change_insertions = insertions.filter((ins) => ins.operation.triggers_location_change);
  const visited_cities: string[] = last_visited_city_before_insertions ? [last_visited_city_before_insertions] : [];
  for (const insertion of location_change_insertions) {
    const city = insertion.operation.city;
    if (city) {
      const idx = visited_cities.indexOf(city);
      if (idx >= 0 && idx != visited_cities.length - 1) {
        return false;
      } else {
        if (!visited_cities.includes(city)) {
          visited_cities.push(city);
        }
      }
    }
  }
  return true;
}

/** Tages-Millisekunden als "HH:MM" formatieren (für Warnmeldungen) */
function formatDayTime(day_ms: number): string {
  const total_minutes = Math.floor(day_ms / 60000);
  const hours = Math.floor(total_minutes / 60);
  const minutes = total_minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function insertionsMatchConstraints(
  insertions: TourOptimizerInsertion[],
  last_visited_city_before_insertions?: string,
  now_day_ms?: number | null,
): { errors: string[] } {
  let errors: string[] = [];

  errors = errors.concat(
    ...insertions.map((ins) => {
      return insertionMatchesSLAConstraint(ins)
        ? []
        : [`Ticket ${ins.operation.ticket_id} Warnung: SLA ${ins.operation.sla_info} `];
    }),
  );

  // Verspätete Fixtermine: Die Tour wird trotzdem geplant, aber der Dispatcher sieht pro
  // Termin, um wie viele Minuten die Ankunft voraussichtlich zu spät ist. Heute bereits
  // verstrichene Fixtermine sind Vergangenheit und erzeugen keine Warnung
  errors = errors.concat(
    ...insertions.map((ins) => {
      const operation = ins.operation;
      if (!operation.date_fixed) return [];
      const fixed_time_already_passed = now_day_ms != null && now_day_ms > operation.operation_day_ms;
      const lateness_ms = ins.start_time_ms - operation.operation_day_ms;
      if (fixed_time_already_passed || lateness_ms <= 0) return [];
      return [
        `Ticket ${operation.ticket_id} Warnung: Fixtermin ${formatDayTime(operation.operation_day_ms)} wird voraussichtlich um ca. ${Math.round(lateness_ms / 60000)} min verpasst.`,
      ];
    }),
  );

  if (!insertionsMatchesSameCityConstraint(insertions, last_visited_city_before_insertions))
    errors.push('Allg. Warnung: Keine mögliche Tour gefunden, die die Zeitbeschränkung erfüllt und jede PLZ nur ein mal besucht.');

  return { errors };
}

/**
 * Baut aus den bewerteten Routen die zeitlich konkrete Tour (Insertions mit Start-/Endzeiten,
 * Mittagspause, Constraint-Warnungen). Nimmt die erste Route, die alle Constraints erfüllt —
 * sonst die bestbewertete mit Warnungen.
 */
export async function computeOptimizedRoute(
  scored_routes: TourOptimizerScoredRoute[],
  config: {
    start_location_id: string;
    start_location_city: string | null;
    end_location_id: string | null;
    /** Manuell gesetzte Pausen-Position (absolute Auftragsnummer) oder null für Automatik */
    lunch_break_after_operation_number: number | null;
    ctx: TourOptimizerContext;
  },
): Promise<{
  optimized_route: TourOptimizerResult | null;
  used_scored_route: TourOptimizerScoredRoute | null;
  /** true, wenn die manuelle Pausen-Position ungültig war und auf Automatik zurückgefallen wurde */
  reset_manual_lunch_break: boolean;
}> {
  const ctx = config.ctx;
  const sorted_routes = sortScoredRoutes(scored_routes || []);
  let optimized_unconstrained_route: TourOptimizerResult;
  let optimized_constrained_route: TourOptimizerResult;
  let used_scored_route: TourOptimizerScoredRoute;

  // Instanz-Zustand des Aufrufers wird nicht direkt mutiert — Rückfall auf automatische
  // Pausenberechnung wird über das reset-Flag zurückgemeldet
  let lunch_break_after_operation_number = config.lunch_break_after_operation_number;
  let reset_manual_lunch_break = false;

  for (const scored_route of sorted_routes) {
    const route = scored_route.route;

    if (!route.length && !scored_route.sorted_extra_operations_with_appointment_date.length) {
      optimized_constrained_route = optimized_unconstrained_route = { insertions: [], errors: [] };
      used_scored_route = scored_route;
      break;
    }

    const insertions: TourOptimizerInsertion[] = [];
    const already_started_operations = route.filter((operation) => operation.has_started);

    let next_earliest_driving_start_ms: number;
    let last_started_location_id: string;

    if (already_started_operations.length) {
      const last_operation = already_started_operations[already_started_operations.length - 1];
      next_earliest_driving_start_ms = last_operation.started_end_time_ms;
      last_started_location_id = getLastAnchorOperation(already_started_operations)?.location_id || config.start_location_id;
    } else {
      const first_operation = route[0];
      last_started_location_id = config.start_location_id;

      if (first_operation?.date_fixed) {
        // Fixtermin: Feste Uhrzeit verwenden, nicht die Öffnungszeit
        const driving_time_minutes = first_operation.triggers_location_change
          ? (await ctx.getDrivingData(last_started_location_id, first_operation.location_id, false)).duration
          : 0;
        const technician_driving_time_ms = factoredDrivingMs(driving_time_minutes, ctx);
        next_earliest_driving_start_ms = first_operation.operation_day_ms - technician_driving_time_ms;
      } else {
        // Normaler Auftrag: Techniker-Startzeit als Basis, ggf. nach hinten verschieben für Store-Öffnung
        if (first_operation?.triggers_location_change) {
          const driving_time_minutes = (await ctx.getDrivingData(last_started_location_id, first_operation.location_id, false)).duration;
          const technician_driving_time_ms = factoredDrivingMs(driving_time_minutes, ctx);

          // Bei Ankunftszeit: Losfahrzeit = Ankunftszeit - Fahrzeit
          // Bei Losfahrzeit: Losfahrzeit = konfigurierte Zeit
          const technician_departure_ms = ctx.is_arrival_time
            ? ctx.configured_start_ms - technician_driving_time_ms
            : ctx.configured_start_ms;
          const arrival_at_store_ms = ctx.is_arrival_time
            ? ctx.configured_start_ms
            : ctx.configured_start_ms + technician_driving_time_ms;

          // Prüfe Store-Öffnungszeit
          const opening_time_ms = first_operation.opening_day_ms;

          if (opening_time_ms && arrival_at_store_ms < opening_time_ms) {
            // Techniker würde VOR Öffnung ankommen → Start nach hinten verschieben
            // sodass er genau zur Öffnung ankommt
            next_earliest_driving_start_ms = opening_time_ms - technician_driving_time_ms;
          } else {
            next_earliest_driving_start_ms = technician_departure_ms;
          }
        } else {
          // Kein Ortswechsel: Bei Ankunftszeit und Losfahrzeit identisch (keine Fahrzeit)
          next_earliest_driving_start_ms = ctx.configured_start_ms;
        }
      }
    }

    const now_day_ms = ctx.now_day_ms;
    if (now_day_ms != null && now_day_ms > next_earliest_driving_start_ms) {
      next_earliest_driving_start_ms = now_day_ms;
    }

    const not_started_operations = route.filter((operation) => !operation.has_started);
    const last_tour_anchor = getLastAnchorOperation(not_started_operations);
    const last_tour_location_id = last_tour_anchor?.location_id || last_started_location_id;
    // Seed für die PLZ-Prüfung ist die Stadt, in der der Techniker VOR den einzuplanenden
    // Aufträgen zuletzt war (letzter gestarteter Auftrag) — NICHT die letzte Stadt der
    // Gesamt-Tour, sonst meldet die Prüfung bei jeder Tour fälschlich
    // „kehrt in bereits besuchte Stadt zurück"
    const last_started_anchor = getLastAnchorOperation(already_started_operations);
    const last_city_before_insertions = already_started_operations.length
      ? (last_started_anchor ? last_started_anchor.city : config.start_location_city) || undefined
      : undefined;
    const home_driving_minutes = !config.end_location_id
      ? 0
      : (await ctx.getDrivingData(last_tour_location_id, config.end_location_id, false)).duration;
    const technician_home_driving_ms = factoredDrivingMs(home_driving_minutes, ctx);
    let current_tour_time_ms = already_started_operations.reduce((sum, op) => sum + op.started_drive_and_work_ms, 0);
    let fill_tour_with_appointment_date_operations = [...scored_route.sorted_extra_operations_with_appointment_date];

    // ═══════════════════════════════════════════════════════════════════════════
    // OPTIMIERUNG: Aufträge VOR dem ersten Fixtermin an diesen ranziehen
    // ═══════════════════════════════════════════════════════════════════════════
    // Damit keine unnötige Pause zwischen den ersten Aufträgen und dem Fixtermin entsteht
    // ABER: SLA-Aufträge dürfen nicht später als ihre SLA-Uhrzeit starten!

    const first_fixed_date_operation = not_started_operations.find((op) => op.date_fixed);
    if (first_fixed_date_operation && not_started_operations.indexOf(first_fixed_date_operation) > 0) {
      // Es gibt einen Fixtermin und es gibt Aufträge davor
      const first_fixed_idx = not_started_operations.indexOf(first_fixed_date_operation);
      const operations_before_fixed = not_started_operations.slice(0, first_fixed_idx);

      // Rückwärts berechnen: Wie viel Zeit brauchen alle Aufträge vor dem Fixtermin?
      let total_time_before_fixed_ms = 0;
      let calc_location_id = config.start_location_id;

      for (const op of operations_before_fixed) {
        if (op.triggers_location_change) {
          const driving_minutes = (await ctx.getDrivingData(calc_location_id, op.location_id, false)).duration;
          total_time_before_fixed_ms += factoredDrivingMs(driving_minutes, ctx);
          calc_location_id = op.location_id;
        }
        total_time_before_fixed_ms += op.work_time_ms;
      }

      // Fahrzeit zum Fixtermin
      if (first_fixed_date_operation.triggers_location_change) {
        const driving_to_fixed_minutes = (await ctx.getDrivingData(calc_location_id, first_fixed_date_operation.location_id, false)).duration;
        total_time_before_fixed_ms += factoredDrivingMs(driving_to_fixed_minutes, ctx);
      }

      // Optimale Startzeit: Fixtermin-Zeit minus Gesamtzeit davor
      const fixed_time_ms = first_fixed_date_operation.operation_day_ms;
      let optimal_start_ms = fixed_time_ms - total_time_before_fixed_ms;

      // SLA-Begrenzung: Die optimale Startzeit darf nicht dazu führen, dass ein
      // Tages-SLA-Auftrag vor dem Fixtermin zu spät fertig wird
      let cumulative_time_ms = 0;
      calc_location_id = config.start_location_id;

      for (const op of operations_before_fixed) {
        let driving_time_ms = 0;
        if (op.triggers_location_change) {
          const driving_minutes = (await ctx.getDrivingData(calc_location_id, op.location_id, false)).duration;
          driving_time_ms = factoredDrivingMs(driving_minutes, ctx);
          calc_location_id = op.location_id;
        }

        const work_time_ms = op.work_time_ms;

        if (op.same_day_sla_ms != null) {
          const sla_time_ms = op.same_day_sla_ms;
          // Endzeit dieses Auftrags bei der optimalen Startzeit
          const op_end_time_ms = optimal_start_ms + cumulative_time_ms + driving_time_ms + work_time_ms;

          if (op_end_time_ms > sla_time_ms) {
            // Auftrag würde nach SLA fertig werden → Startzeit früher setzen
            const required_start_ms = sla_time_ms - cumulative_time_ms - driving_time_ms - work_time_ms;
            if (required_start_ms < optimal_start_ms) {
              optimal_start_ms = required_start_ms;
            }
          }
        }

        cumulative_time_ms += driving_time_ms + work_time_ms;
      }

      // Nur ranziehen wenn die optimale Startzeit SPÄTER ist als die aktuelle
      // (wir wollen nicht früher starten als nötig)
      if (optimal_start_ms > next_earliest_driving_start_ms) {
        next_earliest_driving_start_ms = optimal_start_ms;
      }
    }

    for (const operation of not_started_operations) {
      // Nicht gestartete Aufträge können frühestens ab „jetzt" beginnen — das gilt auch nach
      // einem bereits verstrichenen Fixtermin, dessen Ende sonst als (vergangene) Basis für
      // die Folge-Aufträge dienen würde
      if (now_day_ms != null && now_day_ms > next_earliest_driving_start_ms) {
        next_earliest_driving_start_ms = now_day_ms;
      }

      let technician_driving_time_ms = 0;
      if (operation.triggers_location_change) {
        const driving_time_minutes = (await ctx.getDrivingData(last_started_location_id, operation.location_id, false)).duration;
        technician_driving_time_ms = factoredDrivingMs(driving_time_minutes, ctx);
        last_started_location_id = operation.location_id;
      }
      let operation_start_ms = next_earliest_driving_start_ms + technician_driving_time_ms;

      const forwarded_appointment_operations: TourOptimizerOperation[] = [];
      for (const appointment_operation of fill_tour_with_appointment_date_operations) {
        const app_day_ms = appointment_operation.operation_day_ms;
        // Termin-Auftrag vorziehen, wenn die aktuelle Operation nicht vor dem Termin fertig wird
        const operation_finishes_before_appointment = operation_start_ms + operation.work_time_ms <= app_day_ms;
        if (!operation_finishes_before_appointment) {
          forwarded_appointment_operations.push(appointment_operation);
          const app_start_time_ms = app_day_ms;
          const app_operation_time_ms = appointment_operation.work_time_ms;
          const app_end_time_ms = app_start_time_ms + app_operation_time_ms;
          insertions.push({
            operation: appointment_operation,
            start_time_ms: app_start_time_ms,
            tour_time_ms: current_tour_time_ms,
            end_time_ms: app_end_time_ms,
          });

          if (app_end_time_ms > next_earliest_driving_start_ms) next_earliest_driving_start_ms = app_end_time_ms;
          current_tour_time_ms += app_operation_time_ms;
        }
      }
      fill_tour_with_appointment_date_operations = fill_tour_with_appointment_date_operations.filter(
        (app_op) => !forwarded_appointment_operations.includes(app_op),
      );

      // Bei Fixterminen: Feste Uhrzeit verwenden, nicht die nächste freie Zeit.
      // Ist der Termin nicht mehr pünktlich erreichbar, zeigt die Timeline die reale
      // (verspätete) Ankunft — verstrichene Fixtermine behalten ihre historische Uhrzeit
      if (operation.date_fixed) {
        const fixed_time_already_passed = now_day_ms != null && now_day_ms > operation.operation_day_ms;
        const arrival_ms = next_earliest_driving_start_ms + technician_driving_time_ms;
        operation_start_ms = fixed_time_already_passed
          ? operation.operation_day_ms
          : Math.max(operation.operation_day_ms, arrival_ms);
      } else {
        operation_start_ms = next_earliest_driving_start_ms + technician_driving_time_ms;
      }

      const operation_time_ms = operation.work_time_ms;
      // Die Zeitbasis darf nie zurückspringen: Ein bereits verstrichener Fixtermin endet in der
      // Vergangenheit — Folge-Aufträge müssen trotzdem nach dem bisherigen Tour-Ende liegen
      next_earliest_driving_start_ms = Math.max(next_earliest_driving_start_ms, operation_start_ms + operation_time_ms);
      insertions.push({
        operation: operation,
        start_time_ms: operation_start_ms,
        tour_time_ms: current_tour_time_ms,
        end_time_ms: next_earliest_driving_start_ms,
      });

      current_tour_time_ms += technician_driving_time_ms + operation_time_ms;
    }

    for (const operation of fill_tour_with_appointment_date_operations) {
      const app_start_time_ms = operation.operation_day_ms;
      const app_operation_time_ms = operation.work_time_ms;
      const app_end_time_ms = app_start_time_ms + app_operation_time_ms;
      insertions.push({
        operation: operation,
        start_time_ms: app_start_time_ms,
        tour_time_ms: current_tour_time_ms,
        end_time_ms: app_end_time_ms,
      });
    }

    //add lunch break
    let lunch_break: { after_operation_idx: number; duration_minutes: number };
    const lunch_config = ctx.getLunchBreakConfig((current_tour_time_ms + technician_home_driving_ms) / 60000);
    if (lunch_config) {
      let shift_start_insertion: TourOptimizerInsertion;

      // lunchBreakAfterOperationNumber und after_operation_idx sind ABSOLUTE Positionen
      // (inkl. bereits gestarteter Aufträge) — die insertions enthalten aber nur die nicht
      // gestarteten, daher wird beim Zugriff der Offset der gestarteten Aufträge abgezogen
      const started_operation_count = already_started_operations.length;
      const has_invalid_lunch_break_idx_config =
        lunch_break_after_operation_number > started_operation_count + insertions.length - 1;
      if (has_invalid_lunch_break_idx_config) {
        reset_manual_lunch_break = true;
        lunch_break_after_operation_number = null;
      }

      if (lunch_break_after_operation_number && lunch_break_after_operation_number < started_operation_count) {
        // Manuelle Pausen-Position läge innerhalb der bereits gestarteten Aufträge →
        // dort ist keine Verschiebung mehr möglich, auf automatische Berechnung zurückfallen
        reset_manual_lunch_break = true;
        lunch_break_after_operation_number = null;
      }

      if (lunch_break_after_operation_number) {
        shift_start_insertion = insertions[lunch_break_after_operation_number - started_operation_count];
      } else {
        shift_start_insertion = insertions.find(
          (insertion) => insertion.tour_time_ms > lunch_config.after_tour_minute * 60000 && route.indexOf(insertion.operation) != 0,
        );
        if (!shift_start_insertion && insertions.length && route.length > 1) {
          // If no valid operation found, take the last insertion if the tour has at least 2 operations
          shift_start_insertion = insertions[insertions.length - 1];
        }
      }

      if (shift_start_insertion) {
        const shift_start_idx = insertions.indexOf(shift_start_insertion);
        lunch_break = {
          after_operation_idx: started_operation_count + shift_start_idx - 1,
          duration_minutes: lunch_config.minutes,
        };
        shiftInsertionsByMinutes(insertions, shift_start_idx, lunch_config.minutes);
      } else {
        lunch_break = {
          after_operation_idx: lunch_break_after_operation_number || 0,
          duration_minutes: lunch_config.minutes,
        };
      }
    }

    const { errors } = insertionsMatchConstraints(insertions, last_city_before_insertions, now_day_ms);

    if (sorted_routes.indexOf(scored_route) == 0) {
      optimized_unconstrained_route = {
        errors: errors,
        insertions: insertions,
        lunch_break: lunch_break,
      };
      used_scored_route = scored_route;
    }

    if (!errors.length) {
      optimized_constrained_route = {
        errors: errors,
        insertions: insertions,
        lunch_break: lunch_break,
      };
      used_scored_route = scored_route;
      break;
    }
  }

  // Keine einzige machbare Permutation (z. B. Fixtermin zeitlich unerreichbar):
  // definiert zurückgeben statt auf undefined zuzugreifen — der Aufrufer behandelt den Fall
  if (!optimized_constrained_route && !optimized_unconstrained_route) {
    return { optimized_route: null, used_scored_route: null, reset_manual_lunch_break };
  }

  return {
    optimized_route: optimized_constrained_route || optimized_unconstrained_route,
    used_scored_route,
    reset_manual_lunch_break,
  };
}
